import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDashboard } from "@/lib/api";
import { getCompareIds, clearCompare, toggleCompare } from "@/lib/storage";
import { Mascot } from "@/components/Mascot";
import { Trash2, Plus, X, Wind, Thermometer, Mountain, Droplets, Cloud } from "lucide-react";

export default function ComparePage() {
  const [data, setData] = useState(null);
  const [ids, setIds] = useState(getCompareIds());
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    fetchDashboard().then(setData).catch(() => {});
    const onC = (e) => setIds(e.detail || []);
    window.addEventListener("creep:compare", onC);
    return () => window.removeEventListener("creep:compare", onC);
  }, []);

  const stationsData =
    data?.stations?.filter((s) => ids.includes(s.station.id)) || [];
  const available = data?.stations?.filter((s) => !ids.includes(s.station.id)) || [];

  const rows = [
    { key: "temperature", label: "Temperatura", icon: Thermometer, format: (s) => `${Math.round(s.weather.temperature)}°C`, best: "min", from: (s) => s.weather.temperature },
    { key: "feels", label: "Sensación térmica", icon: Thermometer, format: (s) => `${Math.round(s.weather.feels_like)}°C`, from: (s) => s.weather.feels_like },
    { key: "wind", label: "Viento", icon: Wind, format: (s) => `${Math.round(s.weather.wind_speed)} km/h`, best: "min", from: (s) => s.weather.wind_speed },
    { key: "humidity", label: "Humedad", icon: Droplets, format: (s) => `${Math.round(s.weather.humidity)}%`, from: (s) => s.weather.humidity },
    { key: "precip", label: "Prob. lluvia", icon: Cloud, format: (s) => `${s.weather.precipitation_probability ?? 0}%`, best: "min", from: (s) => s.weather.precipitation_probability },
    { key: "sky", label: "Estado del cielo", format: (s) => s.weather.sky, from: (s) => s.weather.sky },
    { key: "freezing", label: "Cota de nieve", icon: Mountain, format: (s) => s.weather.freezing_level_m != null ? `${Math.round(s.weather.freezing_level_m)} m` : "—", best: "min", from: (s) => s.weather.freezing_level_m },
    { key: "ski", label: "Ski hoy", format: (s) => `${s.ski?.score ?? 0} · ${s.ski?.label ?? "—"}`, best: "max", from: (s) => s.ski?.score ?? 0 },
  ];

  const highlightIds = (row) => {
    if (!row.best) return new Set();
    const available = stationsData.filter((s) => s.available !== false);
    const values = available.map(row.from).filter((v) => typeof v === "number");
    if (values.length === 0) return new Set();
    const target = row.best === "max" ? Math.max(...values) : Math.min(...values);
    return new Set(
      available.filter((s) => row.from(s) === target).map((s) => s.station.id)
    );
  };

  return (
    <div data-testid="compare-page">
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-violet-300/80 mb-2">Comparador</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Duelo de estaciones</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Compara hasta 4 estaciones. La casilla brillante indica el mejor valor de cada fila.
          </p>
        </div>
        {ids.length > 0 && (
          <button
            data-testid="clear-compare"
            onClick={clearCompare}
            className="creep-btn-ghost inline-flex items-center gap-2 text-sm"
          >
            <Trash2 size={14} /> Vaciar
          </button>
        )}
      </header>

      {ids.length === 0 ? (
        <div className="creep-glass p-10 text-center" data-testid="compare-empty">
          <Mascot variant="alert" size={140} />
          <div className="mt-4 text-white/80 font-semibold text-lg">
            Aún no has seleccionado estaciones
          </div>
          <div className="text-white/50 text-sm">
            Usa el icono <span className="text-violet-300">Comparar</span> en cualquier tarjeta o botón inferior.
          </div>
          <button
            data-testid="compare-add-first"
            onClick={() => setShowPicker(true)}
            className="creep-btn mt-6 inline-flex items-center gap-2"
          >
            <Plus size={14} /> Añadir estación
          </button>
        </div>
      ) : (
        <div className="creep-glass-strong overflow-x-auto" data-testid="compare-table-wrap">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-white/8">
                <th className="text-left p-4 text-[10px] uppercase tracking-widest text-violet-300/70 sticky left-0 bg-[#111]/70 backdrop-blur">
                  Métrica
                </th>
                {stationsData.map((s) => (
                  <th key={s.station.id} className="p-3 min-w-[180px]">
                    <div className="flex items-start gap-2">
                      <img
                        src={s.station.image}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover border border-white/10"
                      />
                      <div className="text-left flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-violet-300/70">
                          {s.station.region}
                        </div>
                        <div className="font-bold tracking-tight leading-tight text-white">
                          {s.station.name}
                        </div>
                        <button
                          data-testid={`compare-remove-${s.station.id}`}
                          onClick={() => toggleCompare(s.station.id)}
                          className="mt-1 text-white/50 hover:text-red-300 text-xs inline-flex items-center gap-1"
                        >
                          <X size={12} /> quitar
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
                {ids.length < 4 && (
                  <th className="p-3 min-w-[160px]">
                    <button
                      data-testid="compare-add-more"
                      onClick={() => setShowPicker(true)}
                      className="w-full creep-glass border border-dashed border-violet-500/40 rounded-2xl p-3 hover:bg-violet-500/10 transition-colors flex flex-col items-center gap-1 text-violet-200"
                    >
                      <Plus size={20} />
                      <span className="text-xs">Añadir estación</span>
                    </button>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const winners = highlightIds(row);
                const Icon = row.icon;
                return (
                  <tr key={row.key} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                    <td className="p-4 text-sm text-white/70 sticky left-0 bg-[#111]/60 backdrop-blur border-r border-white/5 whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        {Icon && <Icon size={14} className="text-violet-300" />}
                        {row.label}
                      </div>
                    </td>
                    {stationsData.map((s) => {
                      const isWinner = winners.has(s.station.id);
                      const unavailable = s.available === false;
                      return (
                        <td
                          key={s.station.id}
                          className={`p-3 ${isWinner ? "bg-violet-500/12" : ""}`}
                          data-testid={`compare-cell-${row.key}-${s.station.id}`}
                        >
                          {unavailable ? (
                            <div className="text-sm text-amber-200/70 italic">—</div>
                          ) : (
                            <div className={`text-sm ${isWinner ? "font-bold text-violet-100" : "text-white/85"}`}>
                              {row.format(s)}
                              {isWinner && <span className="ml-2 text-[10px] text-violet-300">▲</span>}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    {ids.length < 4 && <td />}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Picker modal */}
      {showPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(9,9,11,0.7)", backdropFilter: "blur(6px)" }}
          onClick={() => setShowPicker(false)}
          data-testid="compare-picker"
        >
          <div
            className="creep-glass-strong w-full max-w-2xl p-6 max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Añadir estación</h3>
              <button
                data-testid="compare-picker-close"
                onClick={() => setShowPicker(false)}
                className="text-white/60 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            {ids.length >= 4 && (
              <div className="mb-3 text-xs text-amber-200">
                Máximo 4 estaciones. Quita una para añadir otra.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {available.map((s) => (
                <button
                  key={s.station.id}
                  data-testid={`picker-add-${s.station.id}`}
                  onClick={() => {
                    toggleCompare(s.station.id);
                    if (ids.length >= 3) setShowPicker(false);
                  }}
                  disabled={ids.length >= 4}
                  className="creep-glass p-3 text-left hover:bg-violet-500/10 transition-colors flex items-center gap-3 disabled:opacity-40"
                >
                  <img src={s.station.image} alt="" className="w-12 h-12 rounded-md object-cover" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-violet-300/70">
                      {s.station.region}
                    </div>
                    <div className="text-sm font-semibold">{s.station.name}</div>
                    <div className="text-[11px] text-white/50">
                      {Math.round(s.weather.temperature)}°C · Viento {Math.round(s.weather.wind_speed || 0)}km/h
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
